import { useEffect, useState } from 'react';

import { useTheme } from '@/contexts/theme-context';

/**
 * A ```mermaid fence, rendered as a diagram.
 *
 * Mermaid is ~500KB of parser and layout engine, so it is imported dynamically the first time a
 * diagram actually appears rather than shipped in the main bundle — most sessions never contain
 * one. The import is cached by the bundler, so only the first diagram on a page waits for it.
 *
 * A diagram that fails to parse falls back to the source. The agent writes these, so a syntax
 * error is a live possibility, and showing the mermaid source is strictly more useful than an
 * error box: the reader can still follow what was meant.
 */
export function MermaidDiagram({ code }: { code: string }) {
  const { resolvedTheme } = useTheme();
  const [svg, setSvg] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  // Ids must be unique per render or mermaid's internal cache collides across diagrams.
  const [id] = useState(() => `mermaid-${Math.random().toString(36).slice(2, 10)}`);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);

    void (async () => {
      try {
        const mermaid = (await import('mermaid')).default;
        mermaid.initialize({
          startOnLoad: false,
          // `strict` blocks the HTML labels and click handlers a mermaid source could otherwise
          // carry. This content comes from a model, so it is untrusted markup by definition.
          securityLevel: 'strict',
          theme: resolvedTheme === 'dark' ? 'dark' : 'default',
          fontFamily: 'inherit',
        });
        const { svg } = await mermaid.render(id, code);
        if (!cancelled) setSvg(svg);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Re-renders on theme change: mermaid bakes its colours into the SVG, so a cached light-theme
    // diagram would stay light after a switch to dark.
  }, [code, resolvedTheme, id]);

  if (failed) {
    return (
      <div className="overflow-hidden rounded-lg border border-line">
        <div className="border-b border-line bg-surface-2 px-3 py-1.5 text-2xs text-fg-tertiary">
          mermaid — could not be drawn, showing the source
        </div>
        <pre className="overflow-x-auto px-3 py-2.5 font-mono text-2xs leading-relaxed text-fg-secondary">{code}</pre>
      </div>
    );
  }

  if (!svg) {
    return (
      <div className="flex h-24 items-center justify-center rounded-lg border border-line bg-surface-2 text-2xs text-fg-tertiary">
        Drawing diagram…
      </div>
    );
  }

  return (
    <div
      // Mermaid returns a complete SVG document; `securityLevel: 'strict'` is what makes this
      // safe to inject, and the wrapper keeps a wide diagram scrollable instead of overflowing
      // the panel.
      className="[&_svg]:h-auto [&_svg]:max-w-full overflow-x-auto rounded-lg border border-line bg-surface-2 px-3 py-4 text-center"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
